import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

/** Shared chrome. Deliberately quiet — the drawings are the content (§10). */

interface ScreenProps {
  title?: string;
  back?: string | (() => void);
  action?: ReactNode;
  children: ReactNode;
  wide?: boolean;
}

export function Screen({ title, back, action, children, wide }: ScreenProps) {
  const navigate = useNavigate();
  const onBack = typeof back === 'function' ? back : back ? () => navigate(back) : undefined;

  return (
    <div className={`screen ${wide ? 'screen-wide' : ''}`}>
      {(title || onBack || action) && (
        <header className="app-bar">
          {onBack ? (
            <button type="button" className="icon-button" onClick={onBack} aria-label="Back">
              ←
            </button>
          ) : (
            <span className="icon-button-spacer" />
          )}
          <h1>{title}</h1>
          <div className="app-bar-action">{action}</div>
        </header>
      )}
      <main>{children}</main>
    </div>
  );
}

/** green = done/ready, amber = waiting/in progress, red = blocked (§10). */
export type Status = 'ready' | 'waiting' | 'blocked' | 'idle';

export function StatusDot({ status, label }: { status: Status; label?: string }) {
  return (
    <span className={`status status-${status}`}>
      <span className="status-dot" aria-hidden="true" />
      {label ? <span className="status-label">{label}</span> : null}
    </span>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="spinner-wrap">
      <div className="spinner" aria-hidden="true" />
      {label ? <p className="muted">{label}</p> : null}
    </div>
  );
}

export function ErrorNote({ children, onRetry }: { children: ReactNode; onRetry?: () => void }) {
  return (
    <div className="banner banner-error">
      <span>{children}</span>
      {onRetry ? (
        <button type="button" className="link-button" onClick={onRetry}>
          Retry
        </button>
      ) : null}
    </div>
  );
}

export function EmptyNote({ children }: { children: ReactNode }) {
  return <p className="empty-note">{children}</p>;
}
